import { useCallback, useEffect, useRef, useState } from 'react'
import { BridgeError, type RestoreBridge } from './bridge'
import { restoreRunsApi } from './api'

/**
 * Setting up a laptop for restores (the Bridge document "Setting up a laptop from Orchestrate: the
 * setup screen", 15 Oct 2026). The page can only see the package through the plug-in, so the
 * plug-in is checked first (getVersion), then the package (restoreSync with the sign-in pass).
 * Both are checked again whenever the tab regains focus, and the package every 5 seconds after
 * Download is pressed, so each step ticks itself when the engineer comes back.
 */
export const MIN_PLUGIN_VERSION = '1.7.0'
export const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/vaultsentinel-local-execu/mhhjjjlpcoaafeoeobhnabeoljaibafe'
/**
 * The Microsoft Edge Add-ons listing. Off until Microsoft approves it (the setup document, section
 * 5): until then Edge users are given the Chrome Web Store link, with a note. This is the one switch.
 */
export const EDGE_STORE = { live: false, url: 'https://microsoftedge.microsoft.com/addons/detail/mblleoohfogldgnljalholinphcngccj' }
export const PACKAGE_POLL_MS = 5000

export type Browser = 'chrome' | 'edge' | 'other'
export type Platform = 'mac' | 'windows' | 'linux' | 'other'
type Nav = { userAgent?: string; platform?: string; userAgentData?: { brands?: Array<{ brand: string }>; platform?: string } }

export function detectBrowser(nav: Nav = navigator as Nav): Browser {
    const brands = nav.userAgentData?.brands?.map((b) => b.brand) ?? []
    if (brands.length) return brands.includes('Microsoft Edge') ? 'edge' : brands.includes('Google Chrome') ? 'chrome' : 'other'
    const ua = nav.userAgent ?? ''
    if (/\bEdg\//.test(ua)) return 'edge'
    if (/\bChrome\//.test(ua) && !/\b(OPR|Brave|Vivaldi|YaBrowser|SamsungBrowser)\//.test(ua)) return 'chrome'
    return 'other'
}

export function detectPlatform(nav: Nav = navigator as Nav): Platform {
    const text = `${nav.userAgentData?.platform ?? ''} ${nav.platform ?? ''} ${nav.userAgent ?? ''}`
    if (/mac/i.test(text) && !/iphone|ipad/i.test(text)) return 'mac'
    if (/win/i.test(text)) return 'windows'
    if (/linux|x11/i.test(text) && !/android/i.test(text)) return 'linux'
    return 'other'
}

/** Dotted version numbers compared part by part: -1, 0 or 1. Anything after a hyphen is ignored. */
export function compareVersions(a: string, b: string): number {
    const parts = (v: string) => v.split('-')[0].split('.').map((p) => Number.parseInt(p, 10) || 0)
    const x = parts(a)
    const y = parts(b)
    for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
        if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) < (y[i] ?? 0) ? -1 : 1
    }
    return 0
}

/** The relayError codes about reaching the daemon (section 3). Anything else is treated as daemon_unavailable. */
export const SETUP_CODES = ['daemon_not_installed', 'daemon_wrong_plugin', 'daemon_not_started', 'daemon_not_verified', 'daemon_identity_failed', 'browser_unsupported', 'daemon_unavailable'] as const
export type SetupCode = typeof SETUP_CODES[number]
/** Something answered as the daemon and is not it: do not continue, and record it as an incident. */
export const IMPERSONATION: SetupCode[] = ['daemon_not_verified', 'daemon_identity_failed']

export type PluginState = { state: 'checking' } | { state: 'missing' } | { state: 'old'; version: string } | { state: 'ok'; version: string }
export interface Host { daemonVersion: string | null; agentVersion: string | null; name: string | null }
export type PackageState =
    | { state: 'waiting' }  // the plug-in is not there yet: the package cannot be seen
    | { state: 'checking' }
    | { state: 'ok'; host: Host }
    | { state: 'problem'; code: SetupCode; message: string | null }
    /** A relayError with no code: the plug-in refused the message itself. Not about setup. */
    | { state: 'refused'; message: string }

export function hostFrom(data: unknown): Host {
    const host = (data as { host?: Record<string, unknown> } | undefined)?.host ?? {}
    const text = (v: unknown) => (typeof v === 'string' && v ? v : null)
    return { daemonVersion: text(host.daemonVersion), agentVersion: text(host.agentVersion), name: text(host.name) }
}

export function useLaptopSetup(bridge: RestoreBridge, sessionToken: string, renewPass?: () => void) {
    const [plugin, setPlugin] = useState<PluginState>({ state: 'checking' })
    const [pkg, setPkg] = useState<PackageState>({ state: 'waiting' })
    const [watching, setWatching] = useState(false)
    const token = useRef(sessionToken)
    token.current = sessionToken
    const reported = useRef(new Set<string>())
    const browser = useRef(detectBrowser()).current

    const checkPlugin = useCallback(async (): Promise<PluginState> => {
        const answer = await bridge.getVersion()
        const next: PluginState = !answer ? { state: 'missing' }
            : compareVersions(answer.extensionVersion, MIN_PLUGIN_VERSION) < 0 ? { state: 'old', version: answer.extensionVersion }
                : { state: 'ok', version: answer.extensionVersion }
        setPlugin(next)
        return next
    }, [bridge])

    const checkPackage = useCallback(async () => {
        if (!token.current) return
        try {
            const reply = await bridge.request({ type: 'restoreSync', cursors: [] }, token.current)
            setPkg({ state: 'ok', host: hostFrom(reply.data) })
            setWatching(false)
        } catch (error) {
            const e = error as BridgeError
            if (e.code === 'refused' && e.reason === 'unauthorized') {
                // The daemon is there and refused the pass: Orchestrate's side. Fetch a new one.
                setPkg({ state: 'checking' })
                renewPass?.()
                return
            }
            if (e.code === 'relay_error' && !e.reason) { setPkg({ state: 'refused', message: e.message }); return }
            const code: SetupCode = (SETUP_CODES as readonly string[]).includes(e.reason ?? '') ? e.reason as SetupCode : 'daemon_unavailable'
            setPkg({ state: 'problem', code, message: e.message || null })
            if (IMPERSONATION.includes(code) && !reported.current.has(code)) {
                reported.current.add(code)
                restoreRunsApi.setupIncident({ code, message: e.message || null, browser }).catch(() => { reported.current.delete(code) })
            }
        }
    }, [bridge, browser, renewPass])

    const recheck = useCallback(async () => {
        const next = await checkPlugin()
        if (next.state === 'ok') await checkPackage()
        else setPkg({ state: 'waiting' })
    }, [checkPackage, checkPlugin])

    // On open, and whenever the engineer comes back to this tab.
    useEffect(() => { void checkPlugin() }, [checkPlugin])
    useEffect(() => {
        const onFocus = () => { void recheck() }
        const onVisible = () => { if (document.visibilityState === 'visible') void recheck() }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisible)
        return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible) }
    }, [recheck])

    // The package is checked once the plug-in is there and a sign-in pass is held.
    useEffect(() => {
        if (plugin.state !== 'ok') { setPkg({ state: 'waiting' }); return }
        if (!sessionToken) return
        setPkg((now) => (now.state === 'waiting' ? { state: 'checking' } : now))
        void checkPackage()
    }, [plugin.state, sessionToken, checkPackage])

    // After Download is pressed: every 5 seconds until it works or the page is left.
    useEffect(() => {
        if (!watching || pkg.state === 'ok') return undefined
        const timer = setInterval(() => { if (plugin.state === 'ok') void checkPackage() }, PACKAGE_POLL_MS)
        return () => clearInterval(timer)
    }, [watching, pkg.state, plugin.state, checkPackage])

    return { browser, plugin, pkg, recheck, watchPackage: () => setWatching(true), watching }
}
